import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
} from '@jira-clone/ui'
import { usePaymentTerms, usePaymentTermMutations } from '../../shared/hooks/usePaymentTerms'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import { normalizePaymentSchedule } from '@jira-clone/shared-types'

interface ScheduleItem {
  percentage: number
  days: number
  description?: string
}

interface DraftState {
  id?: string
  name: string
  description: string
  schedule: ScheduleItem[]
  isDefault: boolean
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  description: '',
  schedule: [{ percentage: 100, days: 0, description: 'Full payment on delivery' }],
  isDefault: false,
  isActive: true,
}

function addScheduleRow(setDraft: React.Dispatch<React.SetStateAction<DraftState>>) {
  setDraft((d) => ({
    ...d,
    schedule: [...d.schedule, { percentage: 0, days: 0, description: '' }],
  }))
}

function removeScheduleRow(setDraft: React.Dispatch<React.SetStateAction<DraftState>>, index: number) {
  setDraft((d) => ({
    ...d,
    schedule: d.schedule.filter((_, i) => i !== index),
  }))
}

function updateScheduleRow(
  setDraft: React.Dispatch<React.SetStateAction<DraftState>>,
  index: number,
  field: keyof ScheduleItem,
  value: string | number
) {
  setDraft((d) => ({
    ...d,
    schedule: d.schedule.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
  }))
}

export default function PaymentTermAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: paymentTerms = [], isLoading } = usePaymentTerms({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove, setDefault } = usePaymentTermMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (term: any) => {
    setDraft({
      id: term.id,
      name: term.name,
      description: term.description || '',
      schedule: normalizePaymentSchedule(term.schedule).length > 0
        ? normalizePaymentSchedule(term.schedule)
        : [{ percentage: 100, days: 0, description: 'Full payment on delivery' }],
      isDefault: term.isDefault,
      isActive: term.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Name is required.')
      return
    }
    const totalPercentage = draft.schedule.reduce((sum, item) => sum + (item.percentage || 0), 0)
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error('Schedule percentages must total 100%.')
      return
    }
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        schedule: draft.schedule,
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Payment term updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Payment term created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this payment term' : 'create this payment term'))
    }
  }

  const handleDelete = async (term: any) => {
    if (!confirm(`Delete "${term.name}"? This can't be undone.`)) return
    setDeletingId(term.id)
    try {
      await remove.mutateAsync(term.id)
      toast.success('Payment term deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this payment term'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (term: any) => {
    try {
      await setStatus.mutateAsync({ id: term.id, isActive: !term.isActive })
      toast.success(term.isActive ? 'Payment term deactivated.' : 'Payment term activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle payment term status'))
    }
  }

  const handleSetDefault = async (term: any) => {
    try {
      await setDefault.mutateAsync(term.id)
      toast.success('Default payment term updated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'set as default'))
    }
  }

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Payment Terms</h2>
          <p className="text-sm text-muted-foreground">Manage payment schedules and terms for quotations and invoices.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Payment Term
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search payment terms..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : paymentTerms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No payment terms yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {paymentTerms.map((term) => (
            <Card key={term.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={term.isActive ? 'default' : 'secondary'} className="text-xs">
                        {term.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {term.isDefault && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{term.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {term.description || 'No description'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                        <span className="font-mono">
                          {normalizePaymentSchedule(term.schedule).map((s: ScheduleItem) =>
                            `${s.percentage}% ${s.days > 0 ? `in ${s.days}d` : 'on delivery'}`).join(', ')
                          }
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(term)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {term.isDefault ? (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(term)}
                        title="Set as Default"
                      >
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(term)}
                      title={term.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {term.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(term)}
                      disabled={deletingId === term.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Payment Term' : 'New Payment Term'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="pt-name">Name *</Label>
                <Input
                  id="pt-name"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. 50% Advance, 50% on Delivery"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pt-default">Default</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="pt-default"
                    type="checkbox"
                    checked={draft.isDefault}
                    onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                  />
                  <label htmlFor="pt-default" className="text-sm cursor-pointer">Set as default payment term</label>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pt-description">Description</Label>
              <Textarea
                id="pt-description"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Brief description of the payment term"
                rows={2}
              />
            </div>

            <Separator className="my-2" />

            <h4 className="text-sm font-medium text-foreground">Payment Schedule</h4>
            <p className="text-xs text-muted-foreground">Percentages must total 100%.</p>
            <div className="space-y-2">
              {draft.schedule.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                  <span className="text-xs text-muted-foreground shrink-0 w-6 text-center">{index + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      placeholder="%"
                      value={item.percentage}
                      onChange={(e) => updateScheduleRow(setDraft, index, 'percentage', parseFloat(e.target.value) || 0)}
                      className="w-20"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Input
                      type="number"
                      min={0}
                      placeholder="Days"
                      value={item.days}
                      onChange={(e) => updateScheduleRow(setDraft, index, 'days', parseInt(e.target.value) || 0)}
                      className="w-20"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Input
                      placeholder="Description (optional)"
                      value={item.description || ''}
                      onChange={(e) => updateScheduleRow(setDraft, index, 'description', e.target.value)}
                    />
                  </div>
                  {draft.schedule.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeScheduleRow(setDraft, index)}
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
              {draft.schedule.length < 10 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addScheduleRow(setDraft)}
                  className="w-full"
                >
                  + Add Schedule Item
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Payment Term'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}